import React, { useState } from "react";
import { Card, Stack, Box } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { useUsers } from "@/hooks/useUsers";
import { useApp } from "@/contexts/AppContext";
import { UserInput } from "@/utils/api/UserService";

import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { UserProfileForm } from "@/components/features/UserProfileForm";
import { cloneDeep } from "@/utils";

export const UserProfilePage: React.FC<PageProps> = (props: PageProps) => {
  const {
    user: currentUser,
    setUser: setCurrentUser,
    isAuthenticated,
  } = useApp();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Use the updated useUsers hook
  const { isUpdating, lastError, update, clearError } = useUsers({
    autoLoad: false, // Don't auto-load since we're only updating profile
    isAuthenticated,
  });

  const handleSubmit = async (data: UserInput) => {
    clearError();
    setSuccessMessage(null);

    if (!currentUser?.id) {
      return;
    }

    // Use the new update signature: update(id, params)
    const result = await update(currentUser.id, data);

    if (result) {
      // Update the current user in AppContext with the new data
      const updatedUser = {
        ...currentUser,
        ...data,
        // Convert UserInput fields to User interface fields
        name:
          data.firstName && data.lastName
            ? `${data.firstName} ${data.lastName}`
            : currentUser.username,
      };

      setCurrentUser(updatedUser);
      setSuccessMessage("Profile updated successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleRequestEmailConfirmation = () => {
    // TODO: Implement email confirmation request
    console.log("Email confirmation requested for:", currentUser?.email);
  };

  // Convert AppContext User to UserService User format for the form
  const formUser = cloneDeep(currentUser);

  return (
    <DashboardLayout {...props}>
      <Box>
        <Stack gap="lg">
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <UserProfileForm
              user={formUser}
              loading={false} // No loading since we get user from context
              updating={isUpdating}
              onSubmit={handleSubmit}
              onRequestEmailConfirmation={handleRequestEmailConfirmation}
              error={lastError}
              success={successMessage}
            />
          </Card>
        </Stack>
      </Box>
    </DashboardLayout>
  );
};
